import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";

const CONSENT_KEY = "tirevault_cookie_consent";

type ConsentValue = "accepted" | "rejected" | null;

export const CookieConsentBanner = () => {
  const [consent, setConsent] = useState<ConsentValue>(null);
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    setConsent(stored as ConsentValue);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    setConsent("accepted");
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(CONSENT_KEY, "rejected");
    setConsent("rejected");
    setVisible(false);
  };

  if (consent) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4 md:p-6"
        >
          <div className="mx-auto max-w-2xl">
            <div className="bg-card border border-border rounded-xl shadow-lg p-3 sm:p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                <div className="flex items-center gap-2 sm:block">
                  <Cookie className="h-5 w-5 sm:h-6 sm:w-6 text-accent shrink-0" />
                  <h3 className="font-semibold text-foreground text-sm sm:text-base sm:hidden">{t("cookies.title")}</h3>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-1 hidden sm:block">{t("cookies.title")}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                    {t("cookies.body")}{" "}
                    <Link to="/legal/cookie-policy" className="underline hover:text-foreground transition-colors">
                      {t("cookies.cookiePolicy")}
                    </Link>{" "}
                    {t("cookies.and")}{" "}
                    <Link to="/legal/privacy-policy" className="underline hover:text-foreground transition-colors">
                      {t("cookies.privacyPolicy")}
                    </Link>.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1 sm:flex-none text-xs sm:text-sm" onClick={handleAccept}>
                      {t("cookies.acceptAll")}
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 sm:flex-none text-xs sm:text-sm" onClick={handleReject}>
                      {t("cookies.necessary")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
